import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import { getArticles, saveArticles } from '../utils/storage';

const { FiPackage, FiSearch, FiTrash2, FiEdit2, FiSave, FiX, FiPlus } = FiIcons;

export default function Articles() {
  const [articles, setArticles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [newArticle, setNewArticle] = useState({ name: '', price: '' });

  useEffect(() => {
    setArticles(getArticles().sort((a, b) => a.name.localeCompare(b.name)));
  }, []);

  const formatCurrency = (amount) => {
    return Number(amount).toFixed(2).replace('.', ',') + ' €';
  };

  const handleAdd = (e) => {
    e.preventDefault();
    const updated = [...articles, { 
      ...newArticle, 
      id: Date.now().toString(),
      price: parseFloat(newArticle.price) || 0 
    }];
    saveAndRefresh(updated);
    setNewArticle({ name: '', price: '' });
  };

  const handleDelete = (id) => {
    if (window.confirm('Artikel wirklich löschen?')) {
      const updated = articles.filter(a => a.id !== id);
      saveAndRefresh(updated);
    }
  };

  const handleUpdate = (id, data) => {
    const updated = articles.map(a => a.id === id ? { ...a, ...data, price: parseFloat(data.price) || 0 } : a);
    saveAndRefresh(updated);
    setEditingId(null);
  };

  const saveAndRefresh = (data) => {
    saveArticles(data);
    setArticles(data.sort((a, b) => a.name.localeCompare(b.name)));
  };

  const filtered = articles.filter(a => 
    a.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">Artikelverwaltung</h2>
          <p className="text-slate-500">Standardleistungen und Preise verwalten</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl ring-1 ring-slate-200 shadow-sm h-fit">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <SafeIcon icon={FiPlus} className="text-blue-600" /> Neuer Artikel
          </h3>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Bezeichnung</label>
              <input 
                required 
                placeholder="z.B. Stempelnutzung"
                className="w-full px-4 py-2 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none" 
                value={newArticle.name} 
                onChange={e => setNewArticle({ ...newArticle, name: e.target.value })} 
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Netto-Preis (€)</label>
              <input 
                required 
                type="number" 
                step="0.01"
                placeholder="0,00"
                className="w-full px-4 py-2 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none" 
                value={newArticle.price} 
                onChange={e => setNewArticle({ ...newArticle, price: e.target.value })} 
              />
            </div>
            <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 transition-colors">
              Hinzufügen
            </button>
          </form>
        </div>

        <div className="md:col-span-2 space-y-4">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              type="text" 
              placeholder="Artikel suchen..." 
              className="w-full pl-12 pr-4 py-3 rounded-xl border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none bg-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="bg-white rounded-2xl ring-1 ring-slate-200 overflow-hidden">
            <div className="divide-y divide-slate-100">
              {filtered.map(a => (
                <div key={a.id} className="p-4 hover:bg-slate-50 transition-colors">
                  {editingId === a.id ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <input 
                          className="px-3 py-1.5 rounded border" 
                          value={a.name} 
                          onChange={e => setArticles(articles.map(x => x.id === a.id ? { ...x, name: e.target.value } : x))} 
                        />
                        <input 
                          type="number" 
                          step="0.01"
                          className="px-3 py-1.5 rounded border" 
                          value={a.price} 
                          onChange={e => setArticles(articles.map(x => x.id === a.id ? { ...x, price: e.target.value } : x))} 
                        />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleUpdate(a.id, a)} className="text-green-600 flex items-center gap-1 text-sm font-bold">
                          <SafeIcon icon={FiSave} /> Speichern
                        </button>
                        <button onClick={() => setEditingId(null)} className="text-slate-400 flex items-center gap-1 text-sm font-bold">
                          <SafeIcon icon={FiX} /> Abbruch
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-bold text-slate-900">{a.name}</h4>
                        <p className="text-sm font-mono text-blue-600 font-bold">{formatCurrency(a.price)} (Netto)</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setEditingId(a.id)} className="p-2 text-slate-400 hover:text-blue-600">
                          <SafeIcon icon={FiEdit2} />
                        </button>
                        <button onClick={() => handleDelete(a.id)} className="p-2 text-slate-400 hover:text-red-600">
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {filtered.length === 0 && (
                <div className="p-8 text-center text-slate-400">Keine Artikel gefunden</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}